import { ThemeProvider } from '@mui/material/styles';
import { LicenseInfo } from '@mui/x-license-pro';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import ReactDOM from 'react-dom/client';
import App from './App';
import { shouldRetry } from './api';
import { Chart } from 'chart.js';
import disableConsoleInProduction from './disableConsole';
import './index.css';
import './i18n';
import reportWebVitals from './reportWebVitals';
import './variable.css';

LicenseInfo.setLicenseKey(
  '0ef8ea6b74a437d222cecbcec9d661e8Tz0xMzIzNTcsRT0xODE5MzI0Nzk5MDAwLFM9cHJvLExNPWFubnVhbCxQVj1RMS0yMDI2LFE9MSxBVD1zaW5nbGUsS1Y9Mg=='
);
Chart.defaults.font.family = "'Noto Sans Devanagari', 'Roboto', 'sans-serif'";
disableConsoleInProduction();

const REPLAY_CUSTOM_EVENT = 5;

const scrubDeep = <T,>(value: T): T => JSON.parse(scrubLandingTokens(JSON.stringify(value))) as T;

const scrubBreadcrumb = (breadcrumb: Breadcrumb): Breadcrumb => scrubDeep(breadcrumb);

const scrubEvent = <T extends SentryEvent>(event: T): T => {
  const { request, transaction, breadcrumbs, spans } = event;
  if (request?.url !== undefined) request.url = scrubLandingTokens(request.url);
  if (transaction !== undefined) event.transaction = scrubLandingTokens(transaction);
  if (breadcrumbs !== undefined) event.breadcrumbs = breadcrumbs.map(scrubBreadcrumb);
  if (spans !== undefined) event.spans = scrubDeep(spans);
  return event;
};

init({
  dsn: import.meta.env.VITE_APP_SENTRY_DSN,
  integrations: [
    browserTracingIntegration(),
    replayIntegration({
      beforeAddRecordingEvent: (recording) => (recording.type === REPLAY_CUSTOM_EVENT ? scrubDeep(recording) : recording),
    }),
  ],
  tracesSampleRate: 1.0,
  tracePropagationTargets: ['localhost', /^https:\/\/api.digitalpaani\.com\//],
  replaysSessionSampleRate: 0.01,
  replaysOnErrorSampleRate: 1.0,
  beforeSend: (event) => scrubEvent(event),
  beforeSendTransaction: (event) => scrubEvent(event),
  beforeBreadcrumb: (breadcrumb) => scrubBreadcrumb(breadcrumb),
});

import { PostHogProvider } from 'posthog-js/react';
import Theme from './core-components/theme/theme';
import { browserTracingIntegration, init, replayIntegration } from '@sentry/react';
import type { Breadcrumb, Event as SentryEvent } from '@sentry/react';
import { scrubLandingTokens } from './modules/close-the-loop/constants/privacy';

const options = {
  api_host: import.meta.env.VITE_POSTHOG_ENDPOINT,
  autocapture: false,
  capture_pageview: false,
  capture_pageleave: false,
  capture_web_vitals: false,
  capture_performance: false
}

export const queryClient = new QueryClient({
  defaultOptions: { queries: { retry: shouldRetry } },
});
ReactDOM.createRoot(document.getElementById('root')!).render(
    <QueryClientProvider client={queryClient}>
    <PostHogProvider apiKey={import.meta.env.VITE_POSTHOG_TOKEN} options={options}>
    <ThemeProvider theme={Theme}>
        <App />
      </ThemeProvider>
    </PostHogProvider>
      <ReactQueryDevtools initialIsOpen={true} />
    </QueryClientProvider>
);

reportWebVitals();